import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, addDoc, Timestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../service/firebase';
import { getAllStateNames, getDistrictsByState } from '../data/indiaStatesAndDistricts';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import MetalRateStrip from '../components/MetalRateStrip';
import './RequestAccessPage.css';

const RequestAccessPage = () => {
  const [role, setRole] = useState('buyer'); // 'buyer' | 'seller' | 'operator'
  const allStates = getAllStateNames();

  const [formData, setFormData] = useState({
    businessName: '',
    tradeName: '',
    contactPerson: '',
    designation: 'Owner / Partner',
    email: '',
    phone: '',
    state: 'Gujarat',
    district: 'Surat (Diamond & Bullion Hub)',
    city: '',
    gstin: '',
    pancard: '',
    categoryInterest: 'Gold 22K (916)',
    estimatedTurnover: '₹50 Lakhs - ₹2 Crore',
    notes: '',
  });

  const [loading, setLoading] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [error, setError] = useState('');

  // Save form data to localStorage on mount
  useEffect(() => {
    const pageKey = 'jmiRequestAccessPage';
    const saved = localStorage.getItem(pageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Only restore if we have a fresh session (not submitted)
        if (!submittedId) {
          setFormData(prev => ({ ...prev, ...parsed }));
        }
      } catch (e) {
        console.error('Failed to saved request access data:', e);
        localStorage.removeItem(pageKey);
      }
    }
  }, [submittedId]);

  // Save form data to localStorage when it changes
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      const pageKey = 'jmiRequestAccessPage';
      localStorage.setItem(pageKey, JSON.stringify(formData));
    }
  }, [formData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    const districts = getDistrictsByState(selectedState);
    setFormData((prev) => ({
      ...prev,
      state: selectedState,
      district: districts[0] || '',
    }));
    setError('');
  };

  const generateTrackingId = () => {
    const d = new Date();
    const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
    const rand = Math.floor(1000 + Math.random() * 9000);
    return `JMI-REQ-${dateStr}-${rand}`;
  };

  const checkDuplicate = async (phone) => {
    try {
      const q = query(collection(db, "onboardingRequests"), where("phone", "==", phone));
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (err) {
      console.error("[RequestAccessPage] Error checking duplicate:", err);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const cleanPhone = formData.phone.replace(/[^0-9]/g, "");
    if (cleanPhone.length < 10) {
      setError("Please provide a valid 10-digit mobile number.");
      return;
    }

    if (!formData.businessName.trim() || !formData.contactPerson.trim()) {
      setError("Business legal name and contact person are required.");
      return;
    }

    setLoading(true);
    const trackingId = generateTrackingId();

    // Check for duplicate
    const isDuplicate = await checkDuplicate(cleanPhone);
    if (isDuplicate) {
      setError("An application with this mobile number already exists. Your data has been saved. A JMI officer will contact you shortly.");
      setLoading(false);
      return;
    }

    try {
      await addDoc(collection(db, 'onboardingRequests'), {
        trackingId,
        businessName: formData.businessName.trim(),
        tradeName: formData.tradeName.trim(),
        contactPerson: formData.contactPerson.trim(),
        designation: formData.designation,
        phone: cleanPhone,
        email: formData.email.trim(),
        city: formData.city.trim(),
        district: formData.district || '',
        state: formData.state,
        role,
        gstin: formData.gstin.trim().toUpperCase(),
        pancard: formData.pancard.trim().toUpperCase(),
        categoryInterest: formData.categoryInterest,
        estimatedTurnover: formData.estimatedTurnover,
        notes: formData.notes.trim(),
        status: 'pending',
        createdAt: Timestamp.now(),
        source: 'full_page_apply',
      });

      setSubmittedId(trackingId);
      // Clear localStorage on successful submission
      localStorage.removeItem('jmiRequestAccessPage');
    } catch (err) {
      console.error('[RequestAccessPage] Firestore error:', err);
      setSubmittedId(trackingId);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-wrapper request-access-wrapper">
      <MetalRateStrip />
      <Navbar />

      <main className="request-access-main">
        <div className="jmi-container">
          <div className="access-hero-header">
            <img src="/logo.png" alt="JMI Logo" className="access-header-logo" />
            <div className="access-eyebrow">EXCLUSIVE B2B JEWELLERY ECOSYSTEM</div>
            <h1 className="access-title">
              Apply for <span className="gold-gradient-text">Verified JMI Access</span>
            </h1>
            <p className="access-subtitle">
              Due to regulatory and high-value bullion security standards, JMI does not allow direct unverified self-registration. 
              Submit your business credentials below. SuperAdmin verifies each application before provisioning secure app access.
            </p>
          </div>

          <div className="access-grid-layout">
            {/* Left Column: Form */}
            <div className="access-form-card">
              {submittedId ? (
                <div className="access-success-box">
                  <div className="success-icon-big">✓</div>
                  <div className="success-ref-pill">
                    REF NUMBER: <strong>{submittedId}</strong>
                  </div>
                  <h2>Application Successfully Registered</h2>
                  <p>
                    Thank you for applying to join Jewel Mart India as a <strong>{role.toUpperCase()}</strong>. 
                    Your enterprise file for <strong>{formData.businessName}</strong> has been placed in the SuperAdmin approval queue.
                  </p>
                  <div className="officer-note">
                    📞 A JMI onboarding officer will reach out to you quickly for any assistance.
                  </div>
                  <div className="success-next-steps">
                    <h4>Next Verification Steps:</h4>
                    <ul>
                      <li>JMI Compliance team will verify your trade details against GST & BIS records.</li>
                      <li>Our operations team will contact <strong>+91 {formData.phone}</strong>.</li>
                      <li>Upon approval, your login token will be activated for the JMI Buyer / Seller app.</li>
                    </ul>
                  </div>
                  <div className="success-actions">
                    <Link to="/" className="btn btn-primary">
                      Return to Homepage
                    </Link>
                    <Link to="/blog" className="btn btn-secondary">
                      Read Industry Blog
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="role-selector-pills">
                    <button 
                      type="button"
                      className={`role-pill ${role === 'buyer' ? 'active' : ''}`}
                      onClick={() => setRole('buyer')}
                    >
                      <span className="pill-title">Wholesale Buyer</span>
                      <span className="pill-sub">Retail Jeweller / Showroom</span>
                    </button>
                    <button 
                      type="button"
                      className={`role-pill ${role === 'seller' ? 'active' : ''}`}
                      onClick={() => setRole('seller')}
                    >
                      <span className="pill-title">Manufacturer</span>
                      <span className="pill-sub">Bullion / Casting / Factory</span>
                    </button>
                    <button 
                      type="button"
                      className={`role-pill ${role === 'operator' ? 'active' : ''}`}
                      onClick={() => setRole('operator')}
                    >
                      <span className="pill-title">Operator Hub</span>
                      <span className="pill-sub">Regional Vault & Franchise</span>
                    </button>
                  </div>

                  {error && <div className="form-error-alert">⚠️ {error}</div>}

                  <form onSubmit={handleSubmit} className="access-form">
                    <div className="form-section-title">1. Enterprise Information</div>

                    <div className="form-row-2">
                      <div className="form-field">
                        <label>Business Legal Name *</label>
                        <input 
                          type="text" 
                          name="businessName"
                          placeholder="e.g. Mahalakshmi Jewellers Pvt Ltd" 
                          value={formData.businessName}
                          onChange={handleChange}
                          required 
                        />
                      </div>
                      <div className="form-field">
                        <label>Trade Name / Showroom Name</label>
                        <input 
                          type="text" 
                          name="tradeName"
                          placeholder="e.g. Mahalakshmi Gold & Diamonds" 
                          value={formData.tradeName}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="form-row-2">
                      <div className="form-field">
                        <label>GSTIN (Goods and Services Tax ID)</label>
                        <input 
                          type="text" 
                          name="gstin"
                          placeholder="27AAAAA0000A1Z5" 
                          value={formData.gstin}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="form-field">
                        <label>PAN Card Number</label>
                        <input 
                          type="text" 
                          name="pancard"
                          placeholder="ABCDE1234F" 
                          value={formData.pancard}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="form-section-title">2. Authorized Representative</div>

                    <div className="form-row-2">
                      <div className="form-field">
                        <label>Contact Person Full Name *</label>
                        <input 
                          type="text" 
                          name="contactPerson"
                          placeholder="e.g. Your Name" 
                          value={formData.contactPerson}
                          onChange={handleChange}
                          required 
                        />
                      </div>
                      <div className="form-field">
                        <label>Designation</label>
                        <select 
                          name="designation"
                          value={formData.designation}
                          onChange={handleChange}
                        >
                          <option value="Owner / Proprietor">Owner / Proprietor</option>
                          <option value="Managing Director / Partner">Managing Director / Partner</option>
                          <option value="Chief Procurement Officer">Chief Procurement Officer</option>
                          <option value="Authorised Manager">Authorised Manager</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row-2">
                      <div className="form-field">
                        <label>Primary Mobile Number (For App Login) *</label>
                        <input 
                          type="tel" 
                          name="phone"
                          placeholder="e.g. 9876543210" 
                          value={formData.phone}
                          onChange={handleChange}
                          required 
                        />
                      </div>
                      <div className="form-field">
                        <label>Official Business Email *</label>
                        <input 
                          type="email" 
                          name="email"
                          placeholder="contact@enterprise.com" 
                          value={formData.email}
                          onChange={handleChange}
                          required 
                        />
                      </div>
                    </div>

                    <div className="form-section-title">3. Market Location & Trading Segments</div>

                    <div className="form-row-2">
                      <div className="form-field">
                        <label>State / Union Territory *</label>
                        <select 
                          name="state" 
                          value={formData.state}
                          onChange={handleStateChange}
                          required
                        >
                          {allStates.map((st) => (
                            <option key={st} value={st}>
                              {st}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-field">
                        <label>District *</label>
                        <select 
                          name="district" 
                          value={formData.district}
                          onChange={handleChange}
                          required
                        >
                          {getDistrictsByState(formData.state).map((dist) => (
                            <option key={dist} value={dist}>
                              {dist}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-field" style={{ marginBottom: '18px' }}>
                      <label>City & Market Zone / Showroom Address</label>
                      <input 
                        type="text" 
                        name="city"
                        placeholder="e.g. Zaveri Bazaar / Varachha / Karol Bagh / Soni Bazaar" 
                        value={formData.city}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-row-2">
                      <div className="form-field">
                        <label>Primary Product Interest</label>
                        <select 
                          name="categoryInterest" 
                          value={formData.categoryInterest}
                          onChange={handleChange}
                        >
                          <option value="Gold">Gold (BIS 916 / 750 / 999)</option>
                          <option value="Silver">Silver (BIS 925 Sterling & Artefacts)</option>
                          <option value="Platinum">Platinum (Pt 950 Certified)</option>
                          <option value="Diamond">Diamond (Natural & Lab-Grown Solitaires)</option>
                          <option value="Gemstones">Gemstones (Precious & Semi-Precious)</option>
                          <option value="Pearl">Pearl (Freshwater, South Sea & Basra)</option>
                          <option value="Bandhel">Bandhel (Heritage Craft & Wirework)</option>
                          <option value="Decor">Decor (Showroom Displays & Trays)</option>
                          <option value="Brand">Brand & Jewellery Packaging</option>
                          <option value="Tools">Jewellery Tools (Bench & Assaying)</option>
                          <option value="Machines">Jewellery Machinery (CNC & Laser)</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Estimated Monthly Sourcing Volume</label>
                        <select 
                          name="estimatedTurnover" 
                          value={formData.estimatedTurnover}
                          onChange={handleChange}
                        >
                          <option value="₹10 Lakhs - ₹50 Lakhs">₹10 Lakhs - ₹50 Lakhs</option>
                          <option value="₹50 Lakhs - ₹2 Crore">₹50 Lakhs - ₹2 Crore</option>
                          <option value="₹2 Crore - ₹10 Crore">₹2 Crore - ₹10 Crore</option>
                          <option value="₹10 Crore+ (Institutional)">₹10 Crore+ (Enterprise)</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-field">
                      <label>Additional Notes / Specific Requirements</label>
                      <textarea 
                        name="notes"
                        rows="3"
                        placeholder="Mention any specific karatages, custom casting requirements, or regional delivery hubs needed..."
                        value={formData.notes}
                        onChange={handleChange}
                      />
                    </div>

                    <button 
                      type="submit" 
                      disabled={loading}
                      className="btn btn-primary btn-lg w-full submit-access-btn"
                    >
                      {loading ? 'Submitting Application to SuperAdmin...' : 'Submit Official Access Application →'}
                    </button>
                  </form>
                </>
              )}
            </div>

            {/* Right Column: Benefits & Trust Sidebar */}
            <div className="access-sidebar">
              <div className="access-trust-card">
                <h3>Why JMI Requires Verification</h3>
                <p>
                  As India’s premiere high-ticket jewellery B2B network, all orders involve strict bullion rate fixing, 
                  insured courier custody, and tamper-evident smart logistics.
                </p>

                <div className="trust-benefit-item">
                  <div className="benefit-icon">🛡️</div>
                  <div>
                    <h4>100% BIS Hallmarked Sourcing</h4>
                    <p>Every gram of gold is HUID laser stamped and traceable to certified manufacturers.</p>
                  </div>
                </div>

                <div className="trust-benefit-item">
                  <div className="benefit-icon">📈</div>
                  <div>
                    <h4>Real-time Bullion Locking</h4>
                    <p>Direct spot price lock eliminates market volatility between dispatch and delivery.</p>
                  </div>
                </div>

                <div className="trust-benefit-item">
                  <div className="benefit-icon">🚚</div>
                  <div>
                    <h4>Armed Insured Logistics</h4>
                    <p>Secured door-to-door vault delivery with real-time GPS tamper sensors.</p>
                  </div>
                </div>
              </div>

              <div className="access-support-card">
                <h4>Need Immediate Assistance?</h4>
                <p>Our operator onboarding desk is available from 10:00 AM to 8:00 PM IST (Mon - Sat).</p>
                <div className="support-phone">📞 +91 91520 89000</div>
                <div className="support-email">✉️ onboarding@jewelmartindia.com</div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RequestAccessPage;
